import type { ParamMap } from '@angular/router';
import { LocationSubset } from '@era/stores';
import { EnvironmentFeatures } from '@rocksoft/shared';

export type DeviceCategoryType =
  | 'swapped'
  | 'spare'
  | 'assigned'
  | 'housekeeping'
  | 'staff'
  | 'frontdesk'
  | 'bar'
  | 'kiosk';

export interface DeviceLocationSubsetConfig {
  type: DeviceCategoryType;
  title: string;
  emptyMessage: string;
  featureToggle: (features: EnvironmentFeatures) => boolean;
  locationSubset: LocationSubset;
}

export const DEVICE_LOCATION_SUBSET_CONFIGS: readonly DeviceLocationSubsetConfig[] = [
  {
    type: 'swapped',
    title: 'Swapped (lost mode)',
    emptyMessage: 'No swapped devices.',
    featureToggle: (features) => features.devices?.swapped ?? false,
    locationSubset: LocationSubset.Swapped,
  },
  {
    type: 'spare',
    title: 'Spares',
    emptyMessage: 'No spare devices.',
    featureToggle: () => true,
    locationSubset: LocationSubset.Spare,
  },
  {
    type: 'assigned',
    title: 'Assigned',
    emptyMessage: 'No assigned devices.',
    featureToggle: () => true,
    locationSubset: LocationSubset.Assigned,
  },
  {
    type: 'housekeeping',
    title: 'Housekeeping',
    emptyMessage: 'No housekeeping devices.',
    featureToggle: (features) => features.housekeeping?.enabled ?? false,
    locationSubset: LocationSubset.Housekeeping,
  },
  {
    type: 'staff',
    title: 'Staff',
    emptyMessage: 'No staff devices.',
    featureToggle: (features) => features.staff?.enabled ?? false,
    locationSubset: LocationSubset.Staff,
  },
  {
    type: 'frontdesk',
    title: 'Front Desk',
    emptyMessage: 'No front desk devices.',
    featureToggle: (features) => features.frontdesk?.enabled ?? false,
    locationSubset: LocationSubset.FrontDesk,
  },
  {
    type: 'bar',
    title: 'Bar',
    emptyMessage: 'No bar devices.',
    featureToggle: (features) => features.bar?.enabled ?? false,
    locationSubset: LocationSubset.Bar,
  },
  {
    type: 'kiosk',
    title: 'Kiosk',
    emptyMessage: 'No kiosk devices.',
    featureToggle: (features) => features.kiosk?.enabled ?? false,
    locationSubset: LocationSubset.Kiosk,
  },
];

export const DEFAULT_DEVICE_CATEGORY_TYPES: readonly DeviceCategoryType[] = DEVICE_LOCATION_SUBSET_CONFIGS.map(
  ({ type }) => type
);

export function isDeviceCategoryType(value: string): value is DeviceCategoryType {
  return DEFAULT_DEVICE_CATEGORY_TYPES.includes(value as DeviceCategoryType);
}

export function selectedCategoryTypesFromQuery(queryParamMap: ParamMap): readonly DeviceCategoryType[] {
  const selected = queryParamMap.getAll('categoryType').filter(isDeviceCategoryType);

  return queryParamMap.has('categoryType') ? selected : DEFAULT_DEVICE_CATEGORY_TYPES;
}
